<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            ref="grid"
            grid-theme="ag-theme-alpine-report"
            row-selection="multiple"
            :columnDefs="columnDefs"
            :isAutoWidth="true"
            :getRowStyle="rowStyle"
            :isSearch="false"
            :buttonToolbarClass="[
                'align-items-start flex-md-nowrap',
            ]"
            :row-data="deferredEmails"
            :suppress-row-click-selection="true"
            :defaultSort="sort"
            :defaultDesc="true"            
            @selection-changed="handleSelectionChange"
        >

            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
                v-if="!hasSelectedRows"
            ></date-range-picker>
            <responsive-dropdown
                :items="statusOptions"
                variant="primary"
                class="mr-1 d-xl-flex"
                v-if="!hasSelectedRows"
                >{{ selectedStatusLabel }}                
            </responsive-dropdown>            
            <export-to-excel-button :href="excelHref" v-if="!hasSelectedRows" />
            <div
                class="
                    merge-source-wrapper
                    mb-0
                    subscribers-wrapper
                    d-none d-lg-flex
                    mr-1
                "
                v-if="!hasSelectedRows"
            >
                <form-input
                    id="source"
                    @input="(value) => searchSubscribers(value)"
                    :value="subscriberSearch"
                    placeholder="Search by subscriber"
                    autocomplete="off"
                    class="w-100"
                />
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-if="!!subscriberSearch && busy"
                >
                    <div
                        class="
                            guest-item
                            p-2
                            d-flex
                            align-items-center
                            justify-content-center
                        "
                    >
                        <b-spinner variant="primary" :small="true" />
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="subscribersList.length && subscriberSearch"
                >
                    <div
                        class="guest-item p-2"
                        :key="subscriber.id"
                        v-for="subscriber in subscribersList"
                        @click="
                            selectSubscriber(subscriber.text, subscriber.value)
                        "
                    >
                        <span>{{ subscriber.text }}</span>
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="
                        !subscribersList.length &&
                        !!subscriberSearch &&
                        !selectedSubscriber
                    "
                >
                    <div class="p-2">
                        <span class="text-center">Nothing found</span>
                    </div>
                </div>
            </div>
                
            <bulk-actions
                v-if="hasSelectedRows"
                :selected-count="currentSelectedRows.length"
                :bulk-action-defs="bulkActionDefs"
            />

            <b-button-group class="d-lg-none d-block" v-if="!hasSelectedRows"> 
                <more-filters-dropdown>
                        <div
                            class="
                                merge-source-wrapper
                                mb-0
                                subscribers-wrapper
                                d-flex d-lg-none
                                mr-1
                            "
                        >
                        <form-input
                            id="source"
                            @input="(value) => searchSubscribers(value)"
                            :value="subscriberSearch"
                            placeholder="Search by subscriber"
                            autocomplete="off"
                            class="w-100"
                        />
                        <div
                            class="
                                merge-guests-wrapper
                                subscribers-list-wrapper
                            "
                            v-if="!!subscriberSearch && busy"
                        >
                            <div
                                class="
                                    guest-item
                                    p-2
                                    d-flex
                                    align-items-center
                                    justify-content-center
                                "
                            >
                                <b-spinner
                                    variant="primary"
                                    :small="true"
                                />
                            </div>
                        </div>
                        <div
                            class="
                                merge-guests-wrapper
                                subscribers-list-wrapper
                            "
                            v-else-if="
                                subscribersList.length && subscriberSearch
                            "
                        >
                            <div
                                class="guest-item p-2"
                                :key="subscriber.id"
                                v-for="subscriber in subscribersList"
                                @click="
                                    selectSubscriber(
                                        subscriber.text,
                                        subscriber.value
                                    )
                                "
                            >
                                <span>{{ subscriber.text }}</span>
                            </div>
                        </div>
                        <div
                            class="
                                merge-guests-wrapper
                                subscribers-list-wrapper
                            "
                            v-else-if="
                                !subscribersList.length &&
                                !!subscriberSearch &&
                                !selectedSubscriber
                            "
                        >
                            <div class="p-2">
                                <span class="text-center"
                                    >Nothing found</span
                                >
                            </div>
                        </div>
                        <div v-else />
                    </div>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
    </page>
    <modal title="Send Email(s) Confirm" :id="sendBulkModalId" @ok="handleDeferredEmailBulkSend" @shown="onModalShown">
        Are you sure you want to send the selected deferred email(s)?
    </modal>
    <modal title="Delete Email Confirm" :id="deleteBulkModalId" @ok="handleDeferredEmailBulkDelete" @shown="onModalShown">
        Are you sure you want to delete the selected deferred email(s)?
    </modal>    
</template>

<script>
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import API from '../../../api';
import { formatResponseError } from '../../../utils/api';
import ActionsCellRenderer from './actions-cell-renderer.vue';
import { ADMIN_DEFERRED_EMAILS_REPORT_ENDPOINT } from '../../../endpoints';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    ALL_DATES,
} from '../../generic/date-range-picker/date-range-picker.vue';
import Grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import Modal from '../../generic/modal/modal.vue';
import BulkActions from '../../generic/bulk-actions/bulk-actions.vue';
import { toAmountDisplay } from '../../../utils/numbers';
import { truncate } from '../../../utils/strings';
import FormInput from '../../generic/form-input/form-input.vue';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';

export default {
    components: {
        page,
        Grid,
        DateRangePicker,
        FormInput,
        SearchMultiselect,
        ExportToExcelButton,
        MoreFiltersDropdown,
        ResponsiveDropdown,
        BulkActions,
        Modal,
        actionsCellRenderer: ActionsCellRenderer,
    },
    name: 'AdminDeferredEmailsReport',
    data() {
        return {
            busy: false,
            tabs: [
                {
                    title: 'Deferred Emails Report',
                    active: true,
                },
            ],
            sendBulkModalId: 'send-bulk-modal',
            deleteBulkModalId: 'delete-bulk-modal',
            selectedStatus: 'NOT_SENT',
            selectedStatusLabel: 'Not Sent',
            dateRange: nameToRange(ALL_DATES),
            dateRequestFormat: 'yyyy-MM-dd',
            currentSelectedRows: [],
            predefinedRanges: [LAST_MONTH, LAST_WEEK, LAST_YEAR, THIS_MONTH, THIS_WEEK, THIS_YEAR, TODAY, YESTERDAY, ALL_DATES],
            rowStyle: null,
            subscribersList: [],
            selectedSubscriber: '',
            subscriberSearch: '',
            totalRecords: 0,
            sort: 'created',
            desc: true,
            deferredEmails: [],
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),
        hasSelectedRows() {
            return this.currentSelectedRows.length > 0;
        },
        bulkActionDefs() {
            return [
                {
                    label: 'Delete Emails',
                    group: 'Deferred Emails',
                    onClick: this.showDeferredEmailBulkDeleteModal,
                    disabled: false,
                },
                {
                    label: 'Send Emails',
                    group: 'Deferred Emails',
                    onClick: this.showDeferredEmailBulkSendModal,
                    disabled: this.isSendDisabled,
                },
            ];
        },
        isSendDisabled() {
            return this.currentSelectedRows.some((row) => row.sent === true);
        },
        statusOptions() {
            const defaultStatuses = [
                {
                    value: 'ALL',
                    text: 'All Emails',
                },
                {
                    value: 'SENT',
                    text: 'Sent Emails',
                },
                {
                    value: 'NOT_SENT',
                    text: 'Not Sent Emails',
                },
            ];
            return [...defaultStatuses].map((item) => ({
                label: item.text,
                click: () => {
                    this.selectedStatusLabel = item.text;
                    this.selectedStatus = item.value;
                    this.refresh();
                },
            }));
        },
        columnDefs() {
            return [
                {
                    headerName: '#',
                    field: 'id',
                    maxWidth: 80,
                    minWidth: 80,
                    sortable: false,
                    colSpan: (params) => (params.node.rowPinned ? 3 : 1),
                    valueGetter: (params) => { return params.node.rowIndex + 1;},
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                    checkboxSelection: true,
                },                
                {
                    field: 'created',
                    headerName: 'Timestamp',
                    type: 'dateTimeColumn',
                },
                {
                    field: 'subscriber.id',
                    headerName: 'Subscriber ID',
                    minWidth: 130,
                },
                {
                    field: 'subscriber.email',
                    headerName: 'Subscriber Email',
                    minWidth: 200,
                },
                {
                    field: 'is_custom',
                    headerName: 'Sender',
                    minWidth: 90,
                    cellRenderer: ({ data }) => (data.is_custom ? 'Subscriber' : 'Lodgix'),
                },
                {
                    field: 'subject',
                    headerName: 'Subject',
                    sortable: false,
                    autoWidthExclude: true,
                },
                {
                    field: 'sent_on',
                    headerName: 'Sent On',
                    type: 'dateTimeColumn',
                    minWidth: 150,
                    maxWidth: 150,
                },
                {
                    field: 'is_trigger',
                    headerName: 'Trigger',
                    type: 'yesNoColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: 'sent',
                    headerName: 'Sent',
                    type: 'yesNoColumn',
                    minWidth: 120,
                    maxWidth: 120,
                },
                {
                    field: '',
                    headerName: '',
                    cellRenderer: 'actionsCellRenderer',
                    cellRendererParams: {
                        refresh: this.refresh,
                    },
                    maxWidth: 100,
                    minWidth: 100,
                    sortable: false,
                },
            ];
        },
        excelHref() {
            return `${ADMIN_DEFERRED_EMAILS_REPORT_ENDPOINT()}excel/?start_date=${
                this.startDateRequest
            }&end_date=${
                this.endDateRequest
            }&status=${this.selectedStatus}&subscriber=${this.selectedSubscriber}`;
        },
        endDateRequest() {
            return format(this.dateRange.to, this.dateRequestFormat);
        },
        startDateRequest() {
            return format(this.dateRange.from, this.dateRequestFormat);
        },
    },
    mounted() {
        this.rowStyle = (params) => {
            if (params.node.rowPinned) {
                return { 'font-weight': 'bold' };
            }
        };
        Promise.all([this.fetchSubscribers(), this.fetchDeferredEmails()]);
    },
    methods: {
        onSearchInput: debounce(function() {
            this.refresh();
        }, 500),
        async fetchSubscribers() {
            try {
                this.busy = true;
                const { data } = await API.get(`${ADMIN_DEFERRED_EMAILS_REPORT_ENDPOINT()}subscribers/`, {
                    params: {
                        search: this.subscriberSearch,
                    },
                });
                this.subscribersList = data.map(({ user }) => ({
                    text: `${user.id} - ${user.email}`,
                    value: user.id,
                }));
            } catch (e) {
                this.errorToast({ text: 'Could not fetch subscribers' });
            } finally {
                this.busy = false;
            }
        },
        async fetchDeferredEmails() {
            this.currentSelectedRows = [];
            try {
                this.busy = true;
                const { data } = await API.get(`${ADMIN_DEFERRED_EMAILS_REPORT_ENDPOINT()}grid/`, {
                    params: {
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                        status: this.selectedStatus,
                        subscriber: this.selectedSubscriber,
                    },
                });
                this.deferredEmails = data;
            } catch (e) {
                console.log(e);
                const { generic } = formatResponseError(e);
                this.errorToast({
                    text: generic || `Could not load deferred emails.`,
                });
            } finally {
                this.busy = false;
            }
        },
        selectSubscriber(text, value) {
            this.subscriberSearch = text;
            this.selectedSubscriber = value;
            this.subscribersList = [];
            this.refresh();
        },
        searchSubscribers: debounce(function(value) {
            this.subscriberSearch = value;
            this.selectedSubscriber = '';
            if (value === '') {
                this.refresh();
            } else {
                this.fetchSubscribers();
            }
        }, 500),
        handleDaterangeChange(value) {
            this.dateRange = { ...value };
            this.refresh();
        },
        refresh() {
            this.fetchDeferredEmails();
        },
        handleSelectionChange() {
            const gridApi = this.$refs.grid.gridOptions.api;
            this.currentSelectedRows = gridApi.getSelectedRows();
        },
        showDeferredEmailBulkDeleteModal() {
            this.$bvModal.show(this.deleteBulkModalId);
        },
        async handleDeferredEmailBulkDelete() {
            try {
                this.busy = true;
                await API.post(`${ADMIN_DEFERRED_EMAILS_REPORT_ENDPOINT()}bulk-delete/`, {
                    deferred_emails_ids: this.currentSelectedRows.map((obj) => obj.id),
                });
                this.refresh();
                this.successToast({ text: `Deferred email(s) successfully deleted.` });
            } catch (error) {
                const { generic, fields } = formatResponseError(error);
                this.errorToast({
                    text: `Could not delete deferred email(s).`,
                });
            } finally {
                this.busy = false;
            }
        },
        showDeferredEmailBulkSendModal() {
            this.$bvModal.show(this.sendBulkModalId);
        },
        async handleDeferredEmailBulkSend() {
            try {
                this.busy = true;
                await API.post(`${ADMIN_DEFERRED_EMAILS_REPORT_ENDPOINT()}bulk-send/`, {
                    deferred_emails_ids: this.currentSelectedRows.map((obj) => obj.id),
                });
                this.refresh();
                this.successToast({ text: `Deferred email(s) successfully sent.` });
            } catch (error) {
                const { generic, fields } = formatResponseError(error);
                this.errorToast({
                    text: `Could not send deferred email(s).`,
                });
            } finally {
                this.busy = false;
            }
        },  
    },
};
</script>

<style lang="scss" scoped>
.subscribers-list-wrapper {
    top: 40px;
}
.subscribers-wrapper {
    min-width: 300px;
}
</style>
